<template>
  <div class="deepdee-controls">
    <v-row>
      <v-col class="text-center"> 
        <v-expansion-panels v-model="panel" multiple>   
          <v-expansion-panel :class="colorClass">
            <v-expansion-panel-header 
              class="headerText"
              :class="colorClass"
              @click="openedPanel">
              DIABETIC RETINOPATHY AL ANALYSIS
              <v-icon 
                class="mr-3" 
                size="20px" 
                style="float: right;"
                :class="colorClass"
                @click.stop="openDeepDeeDialog">fas fa-info-circle</v-icon>
              <template v-slot:actions>
                <v-icon :color="colorHex" @click="openedPanel">
                  $expand
                </v-icon>
              </template>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-btn 
                class="modal-cta"
                v-if="!hasAIdata && !loadingData && !hardError && !checkingData"
                text 
                @click="createAIAnalysis">
                Run DR AI Analysis
              </v-btn>
              <div v-else-if="hasAIdata && !hardError">
                <span :style="resultStyle">{{overallResult}}</span>
                <div style="float: right">
                <circle-image-button
                  :btnClass="'primary ' + colorClass"
                  :active="deepDeeHighlight"
                  :image="highlighterIcon"
                  title="Highlighter"
                  @click="toggleDeepDeeHighlight()"/>
                <circle-image-button
                  :btnClass="'primary ' + colorClass"
                  :active="showDeepDeeAnnotation"
                  :image="annotationIcon"
                  title="Annotate Image"
                  @click="toggleAnnotation"/>
                </div>
                <bar-chart 
                  :chart-data="deepDeeDataCollection"
                  :options="options"
                  :styles="chartStyle"
                  :width="200"
                  :height="200"></bar-chart>
                <span>
                  Note: This data and analysis is provided for REFERENCE ONLY and is not intended to replace a consultation with a qualified ophthalmic professional.
                </span>
              </div>
              <v-progress-circular v-else-if="loadingData || checkingData" indeterminate />
              <div v-if="checkingData">Checking for existing data</div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { mapGetters, mapMutations } from "vuex";
import BarChart from "../../charts/BarChart.js"
import circleImageButton from "../../controls/CircleImageButton";
import { ANNOTATION_MODES } from "../../../config/config";
import imageAnnotationHelper from "../../mixins/ImageAnnotationHelper";
import DeepDeeAnalysisChart from "../../../canvas/annotations/deepDeeAnalysisChart.js"

import {
  DEEP_DEE_COLORS
} from "../../../config/config.js";

export default {
  name: "ImageDeepDeeControls",
  components: {
    BarChart,
    circleImageButton
  },
  mixins: [imageAnnotationHelper],
  data() {
    return {
      annotationModes: ANNOTATION_MODES,
      panel: false,
      isOpen: false,
      hasAIdata: false,
      loadingData: false,
      checkingData: false,
      hardError: false,
      timeoutID: 0,
      deepDeeDataCollection: {},
      deepDeeLabels: ["Normal", "LC", "No DR", "Mild", "Moderate", "Severe", "Prolif."],
      deepDeeDataset: [],
      options: {
        legend: {
          display: false
        },
        scales: {
          yAxes: [
            {
              ticks: {
                min: 0,
                max: 1.0,
                fontColor: this.defaultColorTheme == "white" ? "black" : "white"
              }
            }
          ],
          xAxes: [
            {
              ticks: {
                fontColor: this.defaultColorTheme == "white" ? "black" : "white"
              }
            }
          ]
        },
        responsive: true
      },
      overallResult: ""
    };
  },
  computed: {
    ...mapGetters([
      "imageBrowserContext",
      "deepDeeHighlight",
      "currentBrowserImage",
      "isPlaying",
      "annotationOptions",
      "showDeepDeeAnnotation",
      "defaultColorTheme"
    ]),
    ...mapGetters({ "context": "imageBrowserContext"}),
    chartStyle() {
      let theme = this.defaultColorTheme;
      switch (theme) {
        case "white":
          return {
            width: "100%",
            backgroundColor: "#FFFFFF"
          };
        default:  
          return {
            width: "100%",
            backgroundColor: "#212121"
          };
        }
    },
    resultStyle() {
      let style = "font-size: 25px; font-weight: bold;";
      switch (this.overallResult) {
        case "Normal":
          style += "color: " + DEEP_DEE_COLORS[0] + ";";
          break;
        case "LC":
          style += "color: " + DEEP_DEE_COLORS[1] + ";";
          break;
        case "No DR":
          style += "color: " + DEEP_DEE_COLORS[2] + ";";
          break;
        case "Mild":
          style += "color: " + DEEP_DEE_COLORS[3] + ";";
          break;
        case "Moderate":
          style += "color: " + DEEP_DEE_COLORS[4] + ";";
          break;
        case "Severe":
          style += "color: " + DEEP_DEE_COLORS[5] + ";";
          break;
        case "Proliferate":
          style += "color: " + DEEP_DEE_COLORS[6] + ";";
          break;
        default: 
          break;
      }
      return style;
    },
    annotationIcon() {
      if (!this.showDeepDeeAnnotation)
        return this.coloredAssetUrls.annotate;
      return this.activeAnnotationIcons.annotate;
    },
    highlighterIcon() {
      if (!this.deepDeeHighlight)
        return this.coloredAssetUrls.highlighter;
      return this.activeAnnotationIcons.highlighter;
    }
  },
  methods: {
    ...mapMutations(["openDeepDeeDialog"]),
    openedPanel(){
      if(this.isOpen){
        this.isOpen = false;
        return
      }
      this.isOpen = true;
      this.checkingData = true;

      if(this.hasAIdata) return; 

      this.$store.dispatch("getDeepDeeExam", {FlName: this.imageBrowserContext.image.flName, 
      ExSrNo: this.imageBrowserContext.exam.exSrNo, PtSrNo: this.imageBrowserContext.ptSrNo, FileSrNo: this.imageBrowserContext.image.flSrNo})
        .then(response => {
          this.checkingData = false;
          if(response.item){
            var img = new Image(this.imageBrowserContext.image.width, this.imageBrowserContext.image.height);
            img.crossOrigin = "anonymous";
            img.src =  response.item.maskUrl;
            img.width = this.imageBrowserContext.image.width;
            img.height = this.imageBrowserContext.image.height;
            this.$store.commit("setImageMask", img);
            this.setDataset(response.item);
            // Set display test based on results of get request.
            this.checkOverallResult();
            this.hasAIdata = true;
          }
        });
    },
    createAIAnalysis() {
      this.loadingData = true;
     // trigger upload action with image file name and exam id
      this.$store.dispatch("createDeepDeeExam",{FlName: this.imageBrowserContext.image.flName, 
      ExSrNo: this.imageBrowserContext.exam.exSrNo, PtSrNo: this.imageBrowserContext.ptSrNo, FileSrNo: this.imageBrowserContext.image.flSrNo})
        .then(response => {
          if (response.status == 1000) {
            this.hasAIdata = false;
            this.loadingData = false;
          }
          else if (response.status == 2000) {
            this.hasAIdata = false;
            this.loadingData = false;
            this.hardError = true;
          }
          else if(response.item){
            var img = new Image(this.imageBrowserContext.image.width, this.imageBrowserContext.image.height);
            img.crossOrigin = "anonymous";
            img.src =  response.item.maskUrl;
            img.width = this.imageBrowserContext.image.width;
            img.height = this.imageBrowserContext.image.height;
            this.$store.commit("setImageMask", img);
            this.hasAIdata = true;      
            this.loadingData = false;
            this.setDataset(response.item);
            // Set display test based on results of get request.
            this.checkOverallResult();
          }
        });
    },
    setDataset(data) {
      this.deepDeeDataset = [
        data.drNormal, 
        data.lk, 
        data.noDr, 
        data.drMild, 
        data.drModerate, 
        data.drProliferate, 
        data.drSevere
      ];
      this.deepDeeDataCollection = {
        labels: this.deepDeeLabels,
        datasets: [
          {
            backgroundColor: DEEP_DEE_COLORS,
            data: this.deepDeeDataset
          }
        ]
      };
      this.setOptions();
    },
    setOptions() {
      this.options = {
        legend: {
          display: false
        },
        scales: {
          yAxes: [
            {
              ticks: {
                min: 0,
                max: 1.0,
                fontColor: this.defaultColorTheme == "white" ? "black" : "white"
              }
            }
          ],
          xAxes: [
            {
              ticks: {
                fontColor: this.defaultColorTheme == "white" ? "black" : "white"
              }
            }
          ]
        }
      };
    },
    checkOverallResult() {
      let i = this.deepDeeDataCollection.datasets[0].data.indexOf(Math.max(...this.deepDeeDataCollection.datasets[0].data));
      switch (i) {
        case 0:
          this.overallResult = "Normal";
          break;
        case 1:
          this.overallResult = "LC";
          break;
        case 2: 
          this.overallResult = "No DR";
          break;
        case 3:
          this.overallResult = "Mild";
          break;
        case 4:
          this.overallResult = "Moderate";
          break;
        case 5:
          this.overallResult = "Severe";
          break;
        case 6:
          this.overallResult = "Proliferate";
          break;
        default:
          break;
      }
      this.$store.commit("setDiagnosisText", this.overallResult);
      this.$store.commit("setDiagnosisColor", DEEP_DEE_COLORS[i]);
    },
    toggleAnnotation() {
      if (!this.context.deepDeeHighlight)
        this.$store.commit("toggleDeepDeeAnnotation");
      if (this.context.showDeepDeeAnnotation)
        this.$store.commit("setDeepDeeAnnotation", new DeepDeeAnalysisChart({x: 0, y: 0}, this.annotationOptions, this.context));
    }
  },
  mounted() {
    this.openedPanel();
  },
  watch: {
    currentBrowserImage: {
      handler() {
        this.panel = false;
        this.isOpen = false;
        this.hasAIdata = false;
        this.loadingData = false;
        this.hardError = false;
      },
      deep: true
    }
  }
};
</script>

<style lang="scss" scoped>
  .deepdee-controls{
    .v-expansion-panel{
       border-top: 1px solid;

      .v-icon{
        font-size: 10px !important;
      }
    }  
    .col{
      padding-left: 0;
      padding-right: 0;
    }
    .headerText{
      text-transform: uppercase;
    }
  }
</style>
